import type { MonthlyRepeatOn, RepeatPreset } from "./storyRefreshTypes";
import { getMonthlyRepeatOptions } from "./storyScheduleRecurrence";
import type { MonthlyRepeatRule } from "./storyScheduleRecurrence";

/**
 * Constants for the story "Schedule refresh" modal.
 *
 * Hour and timezone options are NOT redefined here — they come from
 * `scheduleConstants.ts` (`HOUR_OPTIONS`, `TIMEZONE_OPTIONS`), which is the
 * single source of truth shared with DeliverStory and the connector schedule
 * editor. Only the preset-driven vocabulary is story-refresh specific.
 */

/** The `Repeat` dropdown — Figma labels, verbatim. */
export const REPEAT_OPTIONS: { value: RepeatPreset; label: string }[] = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

/**
 * Static unit word rendered beside the "Repeat every" stepper. The unit is
 * fixed by the preset, so this is text — not the `UNIT_OPTIONS` dropdown the
 * connector modal uses.
 */
export const UNIT_WORD: Partial<Record<RepeatPreset, string>> = {
  weekly: "Week",
  monthly: "Month",
};

const parseIsoDate = (iso: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

export const dayOfMonthFromIso = (iso: string): number | null =>
  parseIsoDate(iso)?.getDate() ?? null;

/** Collapses a semantic monthly rule to the dropdown option that represents it. */
export const toMonthlyRepeatOn = (rule: MonthlyRepeatRule): MonthlyRepeatOn => {
  if (rule.kind === "last_day") return "last-day";
  if (rule.kind === "day_of_month") return "day-of-month";
  if (rule.ordinal === "last") return "last-weekday";
  return "nth-weekday";
};

export const defaultMonthlyRepeatOn = (
  startsOn: string,
): MonthlyRepeatOn | null => {
  const option = getMonthlyRepeatOptions(startsOn)[0];
  return option ? toMonthlyRepeatOn(option.value) : null;
};

/** Google Calendar-style monthly `Repeat on` options, derived from Starts on. */
export const monthlyRepeatOptions = (
  startsOn: string,
): { value: MonthlyRepeatOn; label: string }[] => {
  return getMonthlyRepeatOptions(startsOn).map((option) => ({
    value: toMonthlyRepeatOn(option.value),
    label: option.label,
  }));
};

/**
 * "Ends → After" occurrence counts. Figma shows "12 occurrences"; the control
 * is a stepper, so this only supplies the unit word.
 */
export const occurrencesLabel = (n: number): string =>
  `${n} ${n === 1 ? "occurrence" : "occurrences"}`;
